import { useEffect, useState } from "react";
import { InputAdornment, TextField, TextFieldProps, useTheme } from "@mui/material";
import { WarningHardIcon } from "src/foundations/icons";
import { SenecBaseTheme } from "src/styles/senecBaseTheme";
import { customTextFieldStyles } from "src/components/inputs/input-field/SenecTextFieldStyles";

export type SenecTextFieldProps = {
    /**
     * Variant of textfield "outlined" or "filled"
     */
    variant?: "outlined" | "filled";
    /**
     * Size of textfield "small" or "medium"
     */
    size?: "small" | "medium";
    /**
     * Toggle for character counter adornment. Only works when inputProps.maxLength is set.
     */
    displayCounter?: boolean;
} & Omit<TextFieldProps, "variant">;

/**
 * SenecTextField
 *
 * Component for a common styled text field based on MUI TextField.
 */
export const SenecTextField = ({ displayCounter, ...props }: SenecTextFieldProps) => {
    const [counter, setCounter] = useState(0);
    useEffect(() => {
        setCounter(((props.value ? props.value : "") as string).length);
    }, [props.value]);

    const theme: SenecBaseTheme = useTheme();
    const helperTextStyle = {
        fontFamily: "EnBW DIN Pro, sans-serif !important",
        fontSize: 14,
    };

    const customClassNameVariant = props.variant === "filled" ? "SenecTextField-filled" : "SenecTextField-outlined";
    const customClassNameSize = props.size === "small" ? "SenecTextField-small" : "SenecTextField-medium";
    const customClassNameWithoutLabel = props.label ? "SenecTextField-with-Label" : "SenecTextField-without-Label";
    const shouldDisplayEndAdornment =
        (displayCounter && props.inputProps?.maxLength) || props.InputProps?.endAdornment || props.error;

    const endAdornment = shouldDisplayEndAdornment && (
        <InputAdornment
            data-testid="input-end-adornment"
            key="input-end-adornment"
            position="end"
            sx={
                props.multiline
                    ? {
                          position: "absolute",
                          bottom: "10px",
                          right: "20px",
                          height: "auto",
                      }
                    : {}
            }
        >
            {displayCounter && props.inputProps?.maxLength && `${counter}/${props.inputProps.maxLength}`}

            {props.InputProps?.endAdornment}

            {props.error && (
                <WarningHardIcon
                    data-testid="error-adornment"
                    color={"error"}
                    sx={{
                        transform: "translateY(-6)",
                        position: "relative",
                        right: props.InputProps?.endAdornment ? "-15px" : "0",
                    }}
                />
            )}
        </InputAdornment>
    );

    return (
        <TextField
            data-testid={props.label ? "input-with-label" : "input-without-label"}
            {...props}
            className={`${customClassNameVariant} ${customClassNameSize} ${customClassNameWithoutLabel}`}
            sx={{
                input: {
                    "&::placeholder": {
                        opacity: 1,
                    },
                },
                ...customTextFieldStyles(theme),
                ...props.sx,
            }}
            variant="outlined"
            FormHelperTextProps={{ sx: helperTextStyle }}
            InputProps={{
                ...props.InputProps,
                endAdornment: endAdornment,
            }}
        />
    );
};
